import { useEffect, useState } from "react";

import {
  copyWeeklyPlannerPreviousWeek,
  fetchWeeklyPlanner,
  fetchWeeklyPlannerWithRetry,
  syncWeeklyPlannerWithSummary,
  type WeeklyPlannerPayload,
} from "@/lib/api";
import type { ModuleKey } from "@/lib/grid-contract";

export function plannerModuleActive(module: ModuleKey): boolean {
  return module === "weekly_planner" || module === "my_projects";
}

export function plannerApiViewForModule(module: ModuleKey): "weekly_planner" | "my_projects" {
  return module === "my_projects" ? "my_projects" : "weekly_planner";
}

type UseWeeklyPlannerArgs = {
  activeModule: ModuleKey;
  selectedWeekStartOn: string;
  sourceClientId: string;
};

export function useWeeklyPlanner({
  activeModule,
  selectedWeekStartOn,
  sourceClientId,
}: UseWeeklyPlannerArgs) {
  const [weeklyPlanner, setWeeklyPlanner] = useState<WeeklyPlannerPayload | null>(null);
  const [plannerFetching, setPlannerFetching] = useState(false);
  const [plannerError, setPlannerError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [copyingPreviousWeek, setCopyingPreviousWeek] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!plannerModuleActive(activeModule)) {
      setPlannerFetching(false);
      return;
    }
    let mounted = true;
    setPlannerError(null);
    setPlannerFetching(true);
    fetchWeeklyPlannerWithRetry(selectedWeekStartOn, plannerApiViewForModule(activeModule))
      .then((payload) => {
        if (!mounted) return;
        setWeeklyPlanner(payload);
      })
      .catch((error) => {
        if (!mounted) return;
        setPlannerError(error instanceof Error ? error.message : "Failed to load planner");
      })
      .finally(() => {
        if (mounted) setPlannerFetching(false);
      });
    return () => {
      mounted = false;
    };
  }, [activeModule, selectedWeekStartOn]);

  async function handleSync() {
    if (!weeklyPlanner) return;
    const startedAt = Date.now();
    setSyncing(true);
    setPlannerError(null);
    setSyncMessage(null);
    try {
      const result = await syncWeeklyPlannerWithSummary(
        weeklyPlanner.week_start_on,
        sourceClientId,
        plannerApiViewForModule(activeModule),
      );
      if (activeModule === "my_projects") {
        const scopedPayload = await fetchWeeklyPlanner(weeklyPlanner.week_start_on, "my_projects");
        setWeeklyPlanner(scopedPayload);
      } else {
        setWeeklyPlanner(result.planner);
      }
      const syncedWeeks = result.sync.weeks_synced.join(", ");
      setSyncMessage(
        `Synced ${result.sync.weeks_synced.length} week(s) [${syncedWeeks}], updated ${result.sync.updated_allocations} allocation(s), refreshed ${result.sync.updated_metrics} user metric(s).`,
      );
    } catch (error) {
      setPlannerError(error instanceof Error ? error.message : "Failed to sync planner");
    } finally {
      const elapsed = Date.now() - startedAt;
      const remaining = Math.max(0, 500 - elapsed);
      window.setTimeout(() => setSyncing(false), remaining);
    }
  }

  async function handleCopyPreviousWeekConfirm() {
    if (!weeklyPlanner || copyingPreviousWeek) return;
    setCopyingPreviousWeek(true);
    setPlannerError(null);
    setSyncMessage(null);
    try {
      const result = await copyWeeklyPlannerPreviousWeek(
        weeklyPlanner.week_start_on,
        sourceClientId,
        plannerApiViewForModule(activeModule),
      );
      setWeeklyPlanner(result.planner);
      setSyncMessage(
        `Copied previous week (${result.copy.source_week_start_on}) into ${result.copy.target_week_start_on}: ` +
          `${result.copy.copied_allocations_count} allocation(s), ${result.copy.copied_row_notes_count} note row(s).`,
      );
    } catch (error) {
      setPlannerError(error instanceof Error ? error.message : "Failed to copy previous week");
    } finally {
      setCopyingPreviousWeek(false);
    }
  }

  return {
    weeklyPlanner,
    setWeeklyPlanner,
    plannerFetching,
    plannerError,
    setPlannerError,
    syncing,
    syncMessage,
    setSyncMessage,
    copyingPreviousWeek,
    handleSync,
    handleCopyPreviousWeekConfirm,
  };
}
